import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../utils/ResourceLoading";
import PMListboxFormik from "../../utils/PMListboxFormik";
import { isRecipientEmail } from "../shared/recipientOptions";
import { FormikSendRecipientPicker } from "../shared/SendRecipientPicker";
import { useSelector } from "react-redux";
import { QuillEditor } from "../../utils/quill-editor";
import { fetchContact } from "../../../api/customer/contact";
import {
  sendOffer,
  getPdfOffer,
  checkSendOfferStatus,
} from "../../../api/offer";
import {
  isOfferFeatureEnabled,
  isOfferNotAuthorized,
  resolveOfferActionErrorMessage,
} from "./utils";

import { flushOfferSaves } from "./utils/offer-save-queue";
import { newClientKey } from "./utils/offer-workspace";
import { resolveOfferLayout } from "./utils/offer-layout";
import {
  classifySendPollResult,
  classifySendTimeout,
  clearSendRecovery,
  clearSendRecoveryIfTerminalSuccess,
  isMailedRecoveryStatus,
  isSendOfferAccepted,
  loadSendRecovery,
  mailedRecoveryMessageKey,
  resolveSendOfferAttempt,
  saveSendRecovery,
} from "./utils/offer-send-job";

export const OfferSendDialog = (props) => {
  const { onClose, open, isLoading, offer, featureFlags } = props;
  const { t } = useTranslation();
  const requestKey = useRef(null);
  const pollTimer = useRef(null);
  const activeJob = useRef(null);
  const pollEpoch = useRef(0);
  /**
   * Sticky recovery keys per offer: mail already left, revision needs
   * finalization. Re-POST send_offer with the same request_key — never mint.
   * @type {React.MutableRefObject<Map<string|number, { requestKey: string, jobId?: string|null }>>}
   */
  const recoveryByOfferId = useRef(new Map());
  const documentLayout = props.documentLayout || resolveOfferLayout(offer);
  const flags = featureFlags || offer?.offer_feature_flags || {};
  const revisionsEnabled = isOfferFeatureEnabled(flags, "offer_revisions");
  const checklistRequiredOnSend = isOfferFeatureEnabled(
    flags,
    "offer_checklist_required_on_send"
  );

  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const [offerType, setOfferType] = useState("offer");
  const [pdfUrl, setPdfUrl] = useState("");
  const [jobStatus, setJobStatus] = useState(null);

  useEffect(() => {
    if (open) {
      const memory =
        offer?.id != null ? recoveryByOfferId.current.get(offer.id) : null;
      const stored = offer?.id != null ? loadSendRecovery(offer.id) : null;
      const recovery = memory?.requestKey ? memory : stored;
      if (recovery?.requestKey) {
        // Resume mailed recovery — keep the original request_key (reload-safe).
        requestKey.current = recovery.requestKey;
        recoveryByOfferId.current.set(offer.id, recovery);
        saveSendRecovery(offer.id, recovery);
        if (recovery.jobId) {
          activeJob.current = { id: recovery.jobId, offerId: offer.id };
        }
        setJobStatus("mailed_needs_followup");
      } else if (
        activeJob.current?.offerId === offer?.id &&
        requestKey.current
      ) {
        // Resume same delivery attempt — never mint a new request_key here.
        pollJobStatus(activeJob.current.id);
      } else {
        activeJob.current = null;
        requestKey.current = newClientKey();
        setJobStatus(null);
      }
    }
    return () => {
      pollEpoch.current++;
      clearTimeout(pollTimer.current);
    };
  }, [open, offer?.id]);

  const initialValues = {
    id: offer?.id,
    sendOffer: true,
    printOfferType: "offer",
    email: "",
    name: "",
    message: "",
    submit: null,
  };

  const currentUser = useSelector((state) => state.session.currentUser);

  const applyPollOutcome = (outcome, statusResponse) => {
    if (outcome.clearActiveJob) {
      activeJob.current = null;
    }
    if (outcome.mintNewRequestKey) {
      requestKey.current = newClientKey();
    }

    if (outcome.kind === "mailed_needs_followup") {
      if (offer?.id != null && requestKey.current) {
        const recovery = {
          requestKey: requestKey.current,
          jobId: activeJob.current?.id || null,
        };
        recoveryByOfferId.current.set(offer.id, recovery);
        saveSendRecovery(offer.id, recovery);
      }
      setJobStatus("mailed_needs_followup");
      props.onRefresh?.(statusResponse);
      toast(t(mailedRecoveryMessageKey(outcome.deliveryStatus)));
      return;
    }

    if (outcome.kind === "completed") {
      if (offer?.id != null) {
        recoveryByOfferId.current.delete(offer.id);
        clearSendRecovery(offer.id);
      }
      setJobStatus("completed");
      props.onRefresh?.(statusResponse);
      toast.success(t("offers.send.success"));
      return;
    }

    if (outcome.kind === "failed") {
      setJobStatus("failed");
      const errorMessage =
        statusResponse?.metadata?.error ||
        resolveOfferActionErrorMessage(
          t,
          statusResponse,
          "offers.send.retry"
        );
      toast.error(errorMessage);
      return;
    }

    if (outcome.kind === "status_unconfirmed") {
      setJobStatus("status_unconfirmed");
      toast.error(t("offers.send.status_unconfirmed"));
      return;
    }

    if (outcome.kind === "in_progress") {
      setJobStatus(outcome.jobStatus || statusResponse?.job_status || "processing");
    }
  };

  const persistMailedRecovery = (response, key) => {
    if (!isMailedRecoveryStatus(response) || offer?.id == null || !key) {
      return false;
    }
    if (response?.job_id) {
      activeJob.current = { id: response.job_id, offerId: offer.id };
    }
    const recovery = {
      requestKey: key,
      jobId: response?.job_id || activeJob.current?.id || null,
    };
    recoveryByOfferId.current.set(offer.id, recovery);
    saveSendRecovery(offer.id, recovery);
    setJobStatus("mailed_needs_followup");
    return true;
  };

  /** Job-less terminal `sent` must drop sticky recovery (Bugbot Medium). */
  const clearRecoveryAfterTerminalSuccess = (response) => {
    if (offer?.id == null) return false;
    const cleared = clearSendRecoveryIfTerminalSuccess(offer.id, response, {
      deleteMemory: (id) => recoveryByOfferId.current.delete(id),
    });
    if (cleared) {
      setJobStatus("completed");
    }
    return cleared;
  };

  const handleSend = async (payload) => {
    await flushOfferSaves(offer.id);

    const attempt = resolveSendOfferAttempt({
      activeJob: activeJob.current,
      requestKey: requestKey.current,
      jobStatus,
    });

    // Unresolved timeout / in-flight: re-poll the same job; reuse request_key
    // so QueueDelivery cannot create a second e-mail for this attempt.
    if (attempt.action === "poll") {
      setJobStatus("processing");
      pollJobStatus(activeJob.current.id);
      return {
        job_id: activeJob.current.id,
        delivery_status: "processing",
        reused_delivery: true,
        request_key: attempt.requestKey,
      };
    }

    // Mailed recovery / needs_follow_up («Fullfør revisjon»): re-POST
    // send_offer with the SAME request_key. Never mint a new key here —
    // API 379 re-enqueues finalization without re-mailing.
    if (attempt.action === "repost_same_key") {
      const response = await sendOffer({
        ...payload,
        layout: documentLayout,
        request_key: attempt.requestKey,
      });
      if (persistMailedRecovery(response, attempt.requestKey)) {
        return {
          ...response,
          resumed_mailed_recovery: true,
          request_key: attempt.requestKey,
        };
      }
      if (clearRecoveryAfterTerminalSuccess(response)) {
        return {
          ...response,
          resumed_mailed_recovery: true,
          request_key: attempt.requestKey,
        };
      }
      if (response?.job_id) {
        activeJob.current = { id: response.job_id, offerId: offer.id };
        const recovery = {
          requestKey: attempt.requestKey,
          jobId: response.job_id,
        };
        recoveryByOfferId.current.set(offer.id, recovery);
        saveSendRecovery(offer.id, recovery);
        setJobStatus("queued");
        pollJobStatus(response.job_id);
      }
      return {
        ...response,
        resumed_mailed_recovery: true,
        request_key: attempt.requestKey,
      };
    }

    if (!requestKey.current) {
      requestKey.current = newClientKey();
    }

    const response = await sendOffer({
      ...payload,
      layout: documentLayout,
      request_key: requestKey.current,
    });

    if (persistMailedRecovery(response, requestKey.current)) {
      return response;
    }

    if (clearRecoveryAfterTerminalSuccess(response)) {
      return response;
    }

    if (response?.job_id) {
      activeJob.current = { id: response.job_id, offerId: offer.id };
      setJobStatus("queued");
      pollJobStatus(response.job_id);
    }

    return response;
  };

  const pollJobStatus = async (jobId) => {
    const epoch = ++pollEpoch.current;
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      if (epoch !== pollEpoch.current) return;
      if (attempts >= maxAttempts) {
        // P1: do NOT clear activeJob / mint a new requestKey on timeout.
        applyPollOutcome(classifySendTimeout());
        return;
      }

      try {
        const statusResponse = await checkSendOfferStatus(jobId);
        if (epoch !== pollEpoch.current) return;

        const hardFailure =
          isOfferNotAuthorized(statusResponse) ||
          Boolean(statusResponse?.error) ||
          Boolean(statusResponse?.errors?.length);

        // Transient status-check errors are not confirmed delivery failure —
        // keep keys and keep polling (or mark unconfirmed at maxAttempts).
        if (hardFailure && isOfferNotAuthorized(statusResponse)) {
          applyPollOutcome(
            classifySendPollResult(statusResponse, {
              isHardFailure: () => true,
            }),
            statusResponse
          );
          return;
        }

        if (hardFailure && statusResponse?.job_status == null && !statusResponse?.delivery_status) {
          // Ambiguous error envelope without delivery status — unconfirmed.
          attempts++;
          pollTimer.current = setTimeout(poll, 5000);
          return;
        }

        const outcome = classifySendPollResult(statusResponse, {
          isHardFailure: (payload) =>
            isOfferNotAuthorized(payload) ||
            (payload?.job_status === "failed" ||
              payload?.delivery_status === "failed"),
        });

        if (outcome.kind === "in_progress") {
          applyPollOutcome(outcome, statusResponse);
          attempts++;
          pollTimer.current = setTimeout(poll, 5000);
          return;
        }

        applyPollOutcome(outcome, statusResponse);
      } catch (error) {
        if (epoch !== pollEpoch.current) return;
        console.error("Error checking job status:", error);
        attempts++;
        pollTimer.current = setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleReceiverChange = (receiver, formik) => {
    formik.setFieldValue("email", receiver?.email);
    formik.setFieldValue("name", receiver?.name);
    if (receiver?.printOfferType) {
      formik.setFieldValue("printOfferType", receiver.printOfferType);
      setOfferType(receiver.printOfferType);
      fetchPdfOffer(receiver.printOfferType);
    }
  };

  const fetchPdfOffer = async (offerType) => {
    if (!offer?.id) return false;

    const payload = {
      id: offer?.id,
      params: {
        printOfferType: offerType,
        layout: documentLayout,
      },
    };
    try {
      const response = await getPdfOffer(payload);
      setPdfUrl(response?.pdf_url || "");
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const handleOfferTypeChange = (data) => {
    setOfferType(data?.value);
    if (data?.value) {
      fetchPdfOffer(data.value);
    }
  };

  useEffect(() => {
    const loadContact = async () => {
      if (open && offer?.contact?.id) {
        setIsLoadingContact(true);
        try {
          const response = await fetchContact(offer.contact.id);
          if (response?.data) {
            setContact(response.data);
          }
        } catch (error) {
          console.error("Error fetching contact:", error);
          toast.error(t("offers.send.contact_load_failed"));
        } finally {
          setIsLoadingContact(false);
        }
      } else {
        setContact(null);
      }
    };

    loadContact();
  }, [open, offer?.contact?.id, t]);

  useEffect(() => {
    if (open && offerType) {
      fetchPdfOffer(offerType);
    }
  }, [open, offerType]);

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {t("offers.send.title")}
                    </Dialog.Title>
                    <p className="text-gray-500 text-sm mt-2">
                      {t("offers.send.subtitle")}
                    </p>

                    {revisionsEnabled && (
                      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {t("offers.send.revision_lock_note")}
                      </div>
                    )}

                    {isLoading ? (
                      <ResourceLoading />
                    ) : !offer?.id ? (
                      <p className="mt-3 text-sm text-red-700">
                        {t("offers.send.missing_id")}
                      </p>
                    ) : (
                      <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validate={(values) => isRecipientEmail(values.email) ? {} : { email: t("documentationSend.invalid_email") }}
                        onSubmit={async (values, helpers) => {
                          if (
                            checklistRequiredOnSend &&
                            !offer?.checklist_id &&
                            !offer?.checklist?.id
                          ) {
                            toast.error(t("offers.send.checklist_required"));
                            helpers.setSubmitting(false);
                            return;
                          }
                          try {
                            helpers.setSubmitting(true);

                            const payload = {
                              id: values.id,
                              sendOffer: values.sendOffer,
                              printOfferType: values.printOfferType,
                              email: values.email,
                              name: values.name,
                              message: values.message,
                            };

                            const response = await handleSend(payload);

                            if (
                              isOfferNotAuthorized(response) ||
                              response?.error ||
                              response?.errors?.length
                            ) {
                              const errorMessage = resolveOfferActionErrorMessage(
                                t,
                                response,
                                "offers.send.failed"
                              );
                              toast.error(errorMessage);
                              helpers.setStatus({ success: false });
                              helpers.setErrors({ submit: errorMessage });
                              return;
                            }

                            if (!isSendOfferAccepted(response) && !response?.job_id) {
                              helpers.setErrors({ submit: t("offers.send.failed") });
                              toast.error(t("offers.send.failed"));
                              return;
                            }

                            if (isMailedRecoveryStatus(response)) {
                              toast(
                                t(
                                  mailedRecoveryMessageKey(
                                    response?.delivery_status
                                  )
                                )
                              );
                              props.onRefresh?.(response);
                              onClose();
                            } else if (response?.job_id) {
                              if (!response?.reused_delivery) {
                                toast.success(t("offers.send.queued"));
                              } else {
                                toast(t("offers.send.status_check_resumed"));
                              }
                            } else {
                              const revisionNumber =
                                response?.revision_number ??
                                response?.sent_revision?.revision_number;
                              const revisionId =
                                response?.revision_id ||
                                response?.offer_revision_id;
                              toast.success(
                                revisionNumber != null
                                  ? t("offers.send.success_with_revision", {
                                      number: revisionNumber,
                                      id: revisionId,
                                    })
                                  : revisionId
                                    ? t("offers.send.success_with_revision", {
                                        number: revisionId,
                                        id: revisionId,
                                      })
                                    : t("offers.send.success")
                              );
                              props.onRefresh?.(response);
                              onClose();
                            }
                            helpers.setStatus({ success: true });
                          } catch (err) {
                            toast.error(
                              resolveOfferActionErrorMessage(
                                t,
                                err,
                                "offers.send.failed"
                              )
                            );
                            console.error(err);
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                          } finally {
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="grid grid-cols-6 gap-6 mt-10">
                              <div className="col-span-6">
                                <FormikSendRecipientPicker
                                  label={t("offers.send.send_to")}
                                  options={[
                                    currentUser?.email && { key: "me", label: t("offers.send.me"), email: currentUser.email, name: currentUser.name, printOfferType: "offer" },
                                    offer?.customer?.email && { key: "customer", label: t("offers.send.customer"), email: offer.customer.email, name: offer.customer.name, printOfferType: "customer_offer" },
                                    { key: "contact", label: isLoadingContact ? t("offers.send.loading") : t("offers.send.contact"), email: contact?.email, name: contact?.name || contact?.displayTitle, printOfferType: "customer_offer", disabled: isLoadingContact || !contact?.email },
                                  ].filter(Boolean)}
                                  formik={formik}
                                  customerId={offer?.customer?.id}
                                  autoFocus
                                  onSelect={(option) => handleReceiverChange(option, formik)}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMListboxFormik
                                  label={t("offers.send.offer_type")}
                                  name="printOfferType"
                                  onChange={handleOfferTypeChange}
                                  options={[
                                    {
                                      name: "printOfferType",
                                      value: "offer",
                                      label: t("offers.send.internal_offer"),
                                    },
                                    {
                                      name: "printOfferType",
                                      value: "customer_offer",
                                      label: t("offers.send.customer_offer"),
                                    },
                                  ]}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                {pdfUrl ? (
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    {t("offers.send.open_pdf")}
                                  </a>
                                ) : (
                                  <p className="text-sm text-gray-500">
                                    {t("offers.send.pdf_preview_pending")}
                                  </p>
                                )}
                              </div>
                              <div className="col-span-6 sm:col-span-6 mb-[200px]">
                                <span className="text-sm px-1">
                                  {t("offers.send.message")}
                                </span>
                                <div className="mt-1">
                                  <QuillEditor
                                    name="message"
                                    rows={4}
                                    {...formik}
                                  />
                                </div>
                              </div>

                              {/* Job Status Indicator */}
                              {jobStatus && (
                                <div className="col-span-6 sm:col-span-6">
                                  <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0">
                                        {jobStatus === "queued" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                        )}
                                        {jobStatus === "processing" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                                        )}
                                        {jobStatus === "completed" && (
                                          <div className="rounded-full h-5 w-5 bg-green-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                        {jobStatus === "failed" && (
                                          <div className="rounded-full h-5 w-5 bg-red-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                        {(jobStatus === "status_unconfirmed" ||
                                          jobStatus === "timeout" ||
                                          jobStatus ===
                                            "mailed_needs_followup") && (
                                          <div className="rounded-full h-5 w-5 bg-amber-500 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.594c.75 1.335-.213 2.997-1.742 2.997H3.48c-1.53 0-2.493-1.662-1.743-2.997L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V8a1 1 0 112 0v3a1 1 0 01-1 1z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900">
                                          {jobStatus === "queued" &&
                                            t("offers.send.queued")}
                                          {jobStatus === "processing" &&
                                            t(
                                              "Generating PDF and sending offer..."
                                            )}
                                          {jobStatus === "completed" &&
                                            t("offers.send.success")}
                                          {jobStatus === "failed" &&
                                            t("offers.send.failed")}
                                          {(jobStatus === "timeout" ||
                                            jobStatus ===
                                              "status_unconfirmed") &&
                                            t("offers.send.status_unconfirmed")}
                                          {jobStatus ===
                                            "mailed_needs_followup" &&
                                            t(
                                              "offers.send.mailed_needs_followup"
                                            )}
                                        </p>
                                        {jobStatus === "queued" ||
                                        jobStatus === "processing" ? (
                                          <p className="text-sm text-gray-500">
                                            {t(
                                              "This may take a few minutes. You can close this dialog."
                                            )}
                                          </p>
                                        ) : null}
                                        {jobStatus === "status_unconfirmed" ||
                                        jobStatus === "timeout" ? (
                                          <p className="text-sm text-gray-500">
                                            {t(
                                              "offers.send.status_unconfirmed_hint"
                                            )}
                                          </p>
                                        ) : null}
                                        {jobStatus ===
                                        "mailed_needs_followup" ? (
                                          <p className="text-sm text-gray-500">
                                            {t(
                                              "offers.send.mailed_needs_followup_hint"
                                            )}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                              <button
                                type="button"
                                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={onClose}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing"
                                }
                              >
                                {t("offers.send.close")}{" "}
                              </button>

                              <button
                                type="button"
                                className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={formik.submitForm}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing" ||
                                  jobStatus === "completed"
                                }
                              >
                                {jobStatus === "queued" ||
                                jobStatus === "processing"
                                  ? t("offers.send.sending")
                                  : jobStatus === "status_unconfirmed" ||
                                      jobStatus === "timeout"
                                    ? t("offers.send.check_status")
                                    : jobStatus === "mailed_needs_followup"
                                      ? t("offers.send.finish_revision")
                                      : t("offers.send.submit")}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};


